<template>
  <div>
    <!-- ===== HISTORIE ===== -->
    <div class="section historie" id="historie">
      <div class="ws-split ws-reveal" v-reveal>
        <div class="ws-split-copy">
          <span class="ws-eyebrow">Historie</span>
          <h1 class="ws-h2 mt-3 mb-5">Hoe het begon</h1>
          <p class="ws-lead">
            Stichting WeShare4Kids is voortgekomen uit de wens van enkele
            toegewijde burgers om iets terug te geven aan de samenleving. Het is
            voor gezinnen waarin een kind lijdt of heeft geleden aan een
            ernstige ziekte moeilijk om de overgang te maken van de
            behandelingsfase terug naar het 'normale' leven. In deze periode
            ontbreekt vaak de vertrouwde gezinsdynamiek en zijn de
            energiereserves uitgeput, terwijl nieuwe rituelen en een nieuwe
            mindset nog moeten worden gevonden.
          </p>
          <p class="ws-lead">
            Wij geloven in het principe van wederkerigheid: door te delen en
            elkaar te helpen, kunnen we samen de lasten verlichten en de weg
            naar genezing en herstel effenen.
          </p>
          <p class="ws-lead">
            Daarom streven we met WeShare4Kids ernaar om een gratis
            ‘gezinsverblijf’ aan te bieden, gericht op het ondersteunen van
            gezinnen na de behandeling van ernstige ziekten bij kinderen. Met uw
            steun kunnen we een positief verschil maken in het leven van
            kinderen en gezinnen die dit het meest nodig hebben. Laten we daarom
            samen ons ‘tweede huis’ delen en zo een beetje bijdragen aan een
            betere toekomst voor alle kinderen.
          </p>
        </div>
        <div class="ws-media">
          <v-img
            :eager="true"
            :src="require('@/assets/stock/spelen.jpg')"
            alt="Spelende kinderen buiten"
            cover
            aspect-ratio="1"
          ></v-img>
        </div>
      </div>
    </div>

    <!-- ===== MISSIE ===== -->
    <div class="section" id="missie">
      <div class="ws-split ws-split--reverse ws-reveal" v-reveal>
        <div class="ws-media">
          <v-img
            :src="require('@/assets/stock/knuffel.jpg')"
            alt="Ouder knuffelt kind"
            cover
            aspect-ratio="1"
          ></v-img>
        </div>
        <div class="ws-split-copy">
          <span class="ws-eyebrow">Missie</span>
          <h1 class="ws-h2 mt-3 mb-5">Een nieuwe start voor het hele gezin</h1>
          <p class="ws-lead">
            Juist omdat in deze situaties niet alleen het kind, maar het hele
            gezin een zware tijd heeft gehad, wil de stichting graag proberen
            voor deze gezinnen een ontspannen en positieve overgang te
            faciliteren en heeft daarbij de volgende missie gedefinieerd:
          </p>
          <p class="ws-mission">
            ‘Wij bieden gezinnen waar een kind ernstig ziek is (geweest) een
            bijdrage aan een nieuwe start, om een moeilijke periode samen te
            kunnen afsluiten, samen weer op te laden en het begin van een nieuwe
            toekomst te kunnen vieren’.
          </p>
          <p class="ws-lead">
            We zijn snel geneigd aan kanker te denken als we spreken over
            ernstig zieke kinderen. Maar er zijn uiteraard meer ernstige ziekten
            waar kinderen mee te maken kunnen krijgen, zoals spierziekten,
            darmziekten, hartaandoeningen en nog vele meer. Alle gaan gepaard
            met vaak ingrijpende behandeltrajecten.
          </p>
        </div>
      </div>
    </div>

    <!-- ===== VISIE ===== -->
    <div class="section" id="visie">
      <div class="ws-split ws-reveal" v-reveal>
        <div class="ws-split-copy">
          <span class="ws-eyebrow">Visie</span>
          <h1 class="ws-h2 mt-3 mb-5">Nieuwe gezinsdynamiek</h1>
          <p class="ws-lead">
            Stichting WeShare4Kids is een initiatief van enkele geëngageerde
            burgers die graag iets terug willen doen voor de samenleving. Voor
            gezinnen, waarin een kind lijdt of geleden heeft aan een ernstige
            ziekte is de overgang van de behandelperiode weer naar het ‘gewone’
            leven vaak zwaar en moeilijk. De vertrouwde gezinsdynamiek is er in
            veel gevallen niet meer en de energie is uitgeput terwijl nieuwe
            rituelen en een nieuwe mindset nog moeten worden gevonden.
          </p>
        </div>
        <div class="ws-media">
          <v-img
            :src="require('@/assets/stock/gezin.jpg')"
            alt="Gezin samen"
            cover
            aspect-ratio="1"
          ></v-img>
        </div>
      </div>
    </div>

    <!-- ===== WERKWIJZE ===== -->
    <div class="section text-center" id="werkwijze">
      <span class="ws-eyebrow">Werkwijze</span>
      <h1 class="ws-h2 mt-3">Hoe het werkt</h1>
      <div class="werkwijze mt-10 ws-stagger" v-reveal>
        <div
          class="ws-card ws-reveal"
          v-for="(step, index) in steps"
          :key="step.n"
          :style="{ '--i': index }"
        >
          <span class="step-tile">
            <v-img src="@/assets/sun.svg" alt="" cover class="step-sun"></v-img>
            <b class="step-num">{{ step.n }}</b>
          </span>
          <h3 class="step-title">{{ step.title }}</h3>
          <p class="ws-lead step-text" v-html="step.text"></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useMeta } from "vue-meta";

export default {
  setup() {
    useMeta({
      title: "Over ons",
    });
  },
  data() {
    return {
      steps: [
        {
          n: 1,
          title: "Aanbod",
          text: "Veel mensen in Nederland (en daarbuiten) hebben een tweede accommodatie waar ze zelf graag bijtanken of vakanties doorbrengen, zoals een huisje, caravan, camper, vakantiehuis, of boot. Eigenaren die deel uitmaken van het WeShare4Kids-initiatief willen hun verblijf beschikbaar stellen aan gezinnen met een ernstig ziek kind, zelfs gratis. Ze dragen de kosten zelf en ervaren voldoening door hun onbaatzuchtige bijdrage aan de samenleving.",
        },
        {
          n: 2,
          title: "Vraag",
          text: "Geïndiceerde gezinnen kunnen <b>via een partnerziekenhuis</b> een aanvraag doen om een lang weekend, een week of wellicht nog langer in één van de geregistreerde accommodaties te verblijven. Van dit verblijf kan het gezin dus kosteloos gebruik maken, krijgt het de mogelijkheid weer op te laden en een happy herstart te beleven na een zware periode van herstel.",
        },
        {
          n: 3,
          title: "Matching",
          text: "Verhuurders melden hun vakantiehuis kosteloos aan op Micazu voor WeShare4Kids. Gezinnen melden zich niet zelf aan: een partnerziekenhuis draagt het gezin voor bij WS4K. Na akkoord van WS4K wordt de aanvraag doorgestuurd naar Micazu. Micazu zoekt vervolgens passende woningen, controleert de beschikbaarheid bij de verhuurders en legt (zo mogelijk drie) voorstellen aan het gezin voor. Na akkoord van het gezin vindt verdere communicatie plaats tussen het gezin en de verhuurder.",
        },
      ],
    };
  },
};
</script>

<style lang="scss" scoped>
.historie {
  margin-top: 5vh;
}

.ws-mission {
  font-family: "Nunito", sans-serif;
  font-style: italic;
  font-weight: 700;
  font-size: 18px;
  line-height: 1.5;
  color: #241b12;
  border-left: 3px solid #ea6b26;
  padding-left: 18px;
  margin: 22px 0;
}

.werkwijze {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 24px;
  text-align: left;
}

.step-tile {
  position: relative;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
}
.step-sun {
  position: absolute;
  inset: 0;
}
.step-num {
  position: relative;
  font-family: "Nunito", sans-serif;
  font-weight: 900;
  font-size: 22px;
  color: #241b12;
}
.step-title {
  font-family: "Nunito", sans-serif;
  font-weight: 800;
  font-size: 22px;
  color: #241b12;
  margin-bottom: 11px;
}
.step-text {
  font-size: 15.5px;
  line-height: 1.62;
}

@media screen and (max-width: 900px) {
  .historie {
    margin-top: 10px;
  }
  .werkwijze {
    grid-template-columns: 1fr;
  }
}
</style>
